import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AddNewIcon3,
  CloseIcon,
  DeleteBin6LineIcon,
  EditLine2Icon,
  InformationFillIcon,
  UploadIcon,
} from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, downloadFile, getFileName, handleApiError, notify } from '~/utils/helper';
import { IEquipment } from '~/utils/interface/equipment';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { IEditUser, IUser } from '~/utils/interface/user';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import dayjs from 'dayjs';
import { useDropzone } from 'react-dropzone';
import userValidation from '~/validation/user';
import userApi from '~/apis/user';
import facilityApi from '~/apis/facility';
import { IFacility } from '~/utils/interface/facility';
import deviceApi from '~/apis/device';
import { BaseScrollbar } from '~/components/common';
import { commonActions } from '~/thunks/common/commonSlice';
import fileApi from '~/apis/file';
import EditFacilityDialog from '~/pages/admin/facilities/components/edit-facility-dialog/EditFacilityDialog';
import DetailFacilityDialog from '~/pages/admin/facilities/components/detail-facility-dialog/DetailFacilityDialog';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import DetailEquipmentDialog from '~/pages/admin/equipment/component/detail-equipment-dialog/DetailEquipmentDialog';
import EditEquipmentDialog from '~/pages/admin/facilities/components/edit-equipment-dialog/EditEquipmentDialog';
import { accountActions } from '~/thunks/account/accountSlice';

type EditUserDialogProps = {
  open: boolean;
  data?: IUser;
  mode: string;
  setAssign?: (open: boolean) => void;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setRefreshData?: (refreshData: boolean) => void;
};

const initialValues: IEditUser = {
  name: '',
  username: '',
  email: '',
  date_of_birth: '',
  contract_id: '',
  name2: '',
  phone: '',
  files: [],
};

const EditUserDialog = (props: EditUserDialogProps) => {
  const { t } = useTranslation('admin/user');
  const dispatch = useAppDispatch();

  const { technician } = useAppSelector((state) => state.account);

  const [facilityList, setFacilityList] = useState<IFacility[]>([]);
  const [equipmentList, setEquipmentList] = useState<IEquipment[]>([]);

  const [userType, setUserType] = useState<string>('client');
  const [editFacilityDialogMode, setEditFacilityDialogMode] = useState<string>(DIALOG_MODE_CREATE);

  const [refreshEditFacilityData, setRefreshEditFacilityData] = useState<boolean>(false);
  const [refreshDeleteFacilityData, setRefreshDeleteFacilityData] = useState<boolean>(false);
  const [refreshEquipmentData, setRefreshEquipmentData] = useState<boolean>(false);

  const [isOpenEditFacilityDialog, setIsOpenEditFacilityDialog] = useState<boolean>(false);
  const [isOpenDeleteFacilityDialog, setIsOpenDeleteFacilityDialog] = useState<boolean>(false);
  const [isOpenDetailFacilityDialog, setIsOpenDetailFacilityDialog] = useState<boolean>(false);

  const [facility, setFacility] = useState<IFacility>();
  const [equipment, setEquipment] = useState<IEquipment>();

  const [isOpenDeleteEquipmentDialog, setIsOpenDeleteEquipmentDialog] = useState<boolean>(false);
  const [isOpenDetailEquipmentDialog, setIsOpenDetailEquipmentDialog] = useState<boolean>(false);

  const [isOpenEditEquipmentDialog, setIsOpenEditEquipmentDialog] = useState<boolean>(false);
  const [refreshEditEquipmentData, setRefreshEditEquipmentData] = useState<boolean>(false);

  useEffect(() => {
    if (
      (props.open && props.data) ||
      refreshEditFacilityData ||
      refreshDeleteFacilityData ||
      refreshEquipmentData ||
      refreshEditEquipmentData
    ) {
      const fetchData = async () => {
        try {
          const res = await facilityApi.getListFacilityByUser(String(props?.data?.username));
          setFacilityList(res.data);

          const deviceRes = await deviceApi.getByUserList(String(props?.data?.username));
          setEquipmentList(deviceRes.data);

          setRefreshDeleteFacilityData(false);
          setRefreshEditFacilityData(false);
          setRefreshEquipmentData(false);
          setRefreshEditEquipmentData(false);
        } catch (error) {
          handleApiError(error);
        }
      };

      fetchData();

      return () => {
        dispatch(accountActions.clearTechnician());
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.open,
    props.data?.username,
    refreshEditFacilityData,
    refreshDeleteFacilityData,
    refreshEquipmentData,
    refreshEditEquipmentData,
  ]);

  const form = useFormik({
    validateOnChange: false,
    validationSchema: userType === 'client' ? userValidation(t).upsertClient : userValidation(t).upsertContract,
    initialValues,
    onSubmit: async (values) => {
      if (props.mode === DIALOG_MODE_CREATE) {
        if (userType !== 'client') {
          delete values.date_of_birth;
          delete values.username;
          values.name = values.name2;
          delete values.name2;
        }

        try {
          if (values.date_of_birth === '') {
            delete values.date_of_birth;
          } else {
            values.date_of_birth = dayjs(values.date_of_birth).format('YYYY-MM-DD');
          }
          await userApi.create(values);
          props.setRefreshData && props.setRefreshData(true);
          notify('success', t('createSuccess'));
          handleSetOpen(false, false);
        } catch (e: any) {
          handleApiError(e, true);
        }
      }

      if (props.mode === DIALOG_MODE_EDIT) {
        try {
          if (values.date_of_birth !== '')
            values.date_of_birth = dayjs(values.date_of_birth).format('YYYY-MM-DD HH:mm:ss');

          await userApi.update(values);
          props.setRefreshData && props.setRefreshData(true);
          handleSetOpen(false, false);
          notify('success', t('updateSuccess'));
        } catch (e: any) {
          handleApiError(e, true);
        }
      }
    },
  });

  useEffect(() => {
    if (props.open && props.data && props.mode === DIALOG_MODE_EDIT) {
      form.setValues({
        name: cast(props.data?.name),
        username: cast(props.data?.username),
        email: cast(props.data?.email),
        date_of_birth: cast(props.data?.date_of_birth),
        contract_id: cast(props.data?.contract_id),
        name2: cast(props.data?.name),
        phone: cast(props.data?.phone),
        files: props.data?.files || [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.data, props.mode]);

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    onDrop: async (acceptedFiles: Array<File>) => {
      dispatch(commonActions.setIsLoading(true));
      try {
        const uploadForm = new FormData();
        uploadForm.append('file', acceptedFiles[0]);

        const response = await fileApi.uploadFiles(uploadForm);
        if (response.data) {
          form.setFieldValue('files', [...(form.values.files || []), response.data.url]);
        }
      } catch (e: any) {
        handleApiError(e);
      }

      dispatch(commonActions.setIsLoading(false));
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserType((event.target as HTMLInputElement).value);
  };

  const handleDeleteFacility = async (locationId: string | number) => {
    try {
      await facilityApi.delete({
        location_id: Number(locationId),
      });
      setRefreshDeleteFacilityData(true);
      setIsOpenDeleteFacilityDialog(false);
      notify('success', t('deleteFacilityDialog.deleteSuccess'));
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDeleteDevice = async () => {
    try {
      if (equipment) {
        await userApi.deleteDevice(cast(props.data?.username), { device_id: equipment?.device_id });
        setRefreshEquipmentData(true);
        setIsOpenDeleteEquipmentDialog(false);
        notify('success', t('deleteEquipmentDialog.deleteSuccess'));
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <Box className='w-full'>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
        <DialogTitle className='bg-[#005F73]'>
          {props.mode === DIALOG_MODE_EDIT ? t('edit.title') : t('edit.add')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component='form'>
            <Box className='w-full'>
              <FormControl>
                <RadioGroup
                  row
                  aria-labelledby='demo-row-radio-buttons-group-label'
                  name='row-radio-buttons-group'
                  value={userType}
                  onChange={handleChange}
                >
                  <FormControlLabel
                    value='client'
                    control={
                      <Radio
                        sx={{
                          color: '#ED1002',
                          '&.Mui-checked': {
                            color: '#ED1002',
                          },
                        }}
                      />
                    }
                    label={t('edit.client')}
                    sx={{
                      '.MuiFormControlLabel-label': {
                        color: '#005F73',
                        fontWeight: '700',
                      },
                    }}
                  />
                  <FormControlLabel
                    value='contract'
                    control={
                      <Radio
                        sx={{
                          color: '#ED1002',
                          '&.Mui-checked': {
                            color: '#ED1002',
                          },
                        }}
                      />
                    }
                    label={t('edit.contract')}
                    sx={{
                      '.MuiFormControlLabel-label': {
                        color: '#005F73',
                        fontWeight: '700',
                      },
                    }}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {userType === 'client' ? (
              <Box className='w-full'>
                <BaseRow className='gap-5 mb-5 !items-center'>
                  <Box className='w-1/2'>
                    <InputLabel required={true}>{t('edit.name')}:</InputLabel>
                  </Box>
                  <TextField
                    className='w-1/2'
                    name='name'
                    autoComplete='off'
                    placeholder={t('edit.namRequired')}
                    value={form.values?.name}
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.name)}
                    helperText={form.errors?.name}
                  />
                </BaseRow>
                <BaseRow className='gap-5 mb-5 !items-center'>
                  <Box className='w-1/2'>
                    <InputLabel required={true}>{t('edit.username')}:</InputLabel>
                  </Box>
                  <TextField
                    className='w-1/2'
                    name='username'
                    autoComplete='off'
                    placeholder={t('edit.usernameRequired')}
                    value={form.values?.username}
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.username)}
                    helperText={form.errors?.username}
                  />
                </BaseRow>
                <BaseRow className='gap-5 mb-5 !items-center'>
                  <Box className='w-1/2'>
                    <InputLabel>{t('edit.email')}:</InputLabel>
                  </Box>
                  <TextField
                    className='w-1/2'
                    name='email'
                    autoComplete='off'
                    placeholder={t('edit.emailRequired')}
                    value={form.values?.email}
                    onChange={form.handleChange}
                  />
                </BaseRow>
                <BaseRow className='gap-5 mb-5 !items-center'>
                  <Box className='w-1/2'>
                    <InputLabel>{t('edit.dateOfBirth')}:</InputLabel>
                  </Box>
                  <Box className='w-1/2'>
                    <BaseDatePicker
                      className='w-full'
                      portalId='datePicker-portalId'
                      value={form.values.date_of_birth}
                      dateFormat='YYYY-MM-DD'
                      placeholder={t('edit.selectDateOfBirth')}
                      onChange={(date) => {
                        form.setFieldValue('date_of_birth', dayjs(date));
                      }}
                    />
                  </Box>
                </BaseRow>
                {props.mode === DIALOG_MODE_EDIT && (
                  <Box>
                    <BaseRow className='gap-5 mb-5 !items-center'>
                      <Box className='w-1/2'>
                        <InputLabel>{t('edit.technician')}:</InputLabel>
                      </Box>
                      <Box className='w-1/2 flex'>
                        <TextField
                          className='w-full pointer-events-none'
                          autoComplete='off'
                          value={technician || props.data?.technician}
                        />
                        <Button
                          variant='contained'
                          className='font-bold rounded-[8px] h-[35px] min-w-[49px] ml-5'
                          onClick={() => {
                            props.setAssign?.(true);
                          }}
                        >
                          {t('edit.assign')}
                        </Button>
                      </Box>
                    </BaseRow>
                    <Box className='w-full'>
                      <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                        <Box className='font-bold text-[#005F73] text-[18px]'>{t('show.facilityList')}</Box>
                        <Box className='flex items-center justify-center'>
                          <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2 mr-2'>
                            {facilityList?.length}
                          </Box>
                          <AddNewIcon3
                            className='cursor-pointer'
                            onClick={() => {
                              setIsOpenEditFacilityDialog(true);
                              setEditFacilityDialogMode(DIALOG_MODE_CREATE);
                            }}
                          />
                        </Box>
                      </BaseRow>
                      <BaseScrollbar maxHeight='250px'>
                        {facilityList?.length ? (
                          <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                            <Table>
                              <TableBody className='divide-y divide-[#005F73]'>
                                {facilityList.map((row, index) => {
                                  return (
                                    <TableRow key={index}>
                                      <TableCell className='border-none'>
                                        <Box className='font-bold'>{row.location_name}</Box>
                                        <Box>{row.full_address}</Box>
                                      </TableCell>
                                      <TableCell className='border-none text-right'>
                                        <Box className='font-bold'>
                                          <Box component='span' className='font-bold text-[#ED1002]'>
                                            {row.device_count}{' '}
                                          </Box>
                                          {t('show.device')}
                                        </Box>
                                        <Box className='font-bold'>
                                          {row.deployment_status === 2 ? (
                                            <Box className='text-[#3D9149]'>{t('facilityStatus.activated')}</Box>
                                          ) : row.deployment_status === 1 ? (
                                            <Box className='text-[#3D9149]'>{t('facilityStatus.deployed')}</Box>
                                          ) : (
                                            <Box className='text-[#72777B]'>{t('facilityStatus.pending')}</Box>
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell className='border-none flex justify-between'>
                                        <IconButton>
                                          <InformationFillIcon
                                            fill='#D9D9D9'
                                            onClick={() => {
                                              setFacility(row);
                                              setIsOpenDetailFacilityDialog(true);
                                            }}
                                          />
                                        </IconButton>
                                        <IconButton>
                                          <EditLine2Icon
                                            onClick={() => {
                                              setFacility(row);
                                              setIsOpenEditFacilityDialog(true);
                                              setEditFacilityDialogMode(DIALOG_MODE_EDIT);
                                            }}
                                          />
                                        </IconButton>
                                        <IconButton>
                                          <DeleteBin6LineIcon
                                            fill='#ED1002'
                                            onClick={() => {
                                              setFacility(row);
                                              setIsOpenDeleteFacilityDialog(true);
                                            }}
                                          />
                                        </IconButton>
                                        <Button
                                          className='text-white font-semibold min-w-[100px] bg-[#ED1002] hover:bg-[#005F73]'
                                          onClick={() => {
                                            setFacility(row);
                                            setIsOpenEditEquipmentDialog(true);
                                          }}
                                        >
                                          {t('addNewEquipment')}
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                        ) : null}
                      </BaseScrollbar>
                    </Box>
                    <Box className='mt-[16px]'>
                      <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                        <Box className='font-bold text-[#005F73] text-[18px]'>{t('show.listEquipment')}</Box>
                        <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2'>
                          {equipmentList?.length}
                        </Box>
                      </BaseRow>
                      <BaseScrollbar maxHeight='250px'>
                        {equipmentList?.length ? (
                          <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                            <Table>
                              <TableBody className='divide-y divide-[#005F73]'>
                                {equipmentList.map((row, index) => {
                                  return (
                                    <TableRow key={index}>
                                      <TableCell className='border-none'>
                                        <Box className='font-bold '>{row.device_code}</Box>
                                        <Box>{row.location_name}</Box>
                                      </TableCell>
                                      <TableCell className='border-none text-right'>
                                        <Box className='font-bold'>{row.device_name}</Box>
                                        <Box className='font-bold'>
                                          <Box
                                            className={row.connect_status === 1 ? 'text-[#3D9149]' : 'text-[#F32A4C]'}
                                          >
                                            {t('connectionStatus.' + row.connect_status)}
                                          </Box>
                                        </Box>
                                      </TableCell>
                                      <TableCell className='border-none flex justify-end'>
                                        <IconButton>
                                          <InformationFillIcon
                                            fill='#D9D9D9'
                                            onClick={() => {
                                              setEquipment(row);
                                              setIsOpenDetailEquipmentDialog(true);
                                            }}
                                          />
                                        </IconButton>
                                        <IconButton>
                                          <DeleteBin6LineIcon
                                            fill='#ED1002'
                                            onClick={() => {
                                              setEquipment(row);
                                              setIsOpenDeleteEquipmentDialog(true);
                                            }}
                                          />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                        ) : null}
                      </BaseScrollbar>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Box className='w-full'>
                <BaseRow className='gap-5 mb-5 !items-center'>
                  <Box className='w-1/2'>
                    <InputLabel required={true}>{t('edit.contractId')}:</InputLabel>
                  </Box>
                  <TextField
                    className='w-1/2'
                    name='contract_id'
                    autoComplete='off'
                    placeholder={t('edit.contractIdRequired')}
                    value={form.values?.contract_id}
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.contract_id)}
                    helperText={form.errors?.contract_id}
                  />
                </BaseRow>
                <BaseRow className='gap-5 mb-5 !items-center'>
                  <Box className='w-1/2'>
                    <InputLabel required={true}>{t('edit.representative')}:</InputLabel>
                  </Box>
                  <TextField
                    className='w-1/2'
                    name='name2'
                    autoComplete='off'
                    placeholder={t('edit.representativeRequired')}
                    value={form.values?.name2}
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.name2)}
                    helperText={form.errors?.name2}
                  />
                </BaseRow>
                <BaseRow className='gap-5 mb-5 !items-center'>
                  <Box className='w-1/2'>
                    <InputLabel required={true}>{t('edit.email')}:</InputLabel>
                  </Box>
                  <TextField
                    className='w-1/2'
                    name='email'
                    autoComplete='off'
                    placeholder={t('edit.emailRequired')}
                    value={form.values?.email}
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.email)}
                    helperText={form.errors?.email}
                  />
                </BaseRow>
                <BaseRow className='gap-5 mb-5 !items-center'>
                  <Box className='w-1/2'>
                    <InputLabel required={true}>{t('edit.phone')}:</InputLabel>
                  </Box>
                  <TextField
                    className='w-1/2'
                    name='phone'
                    autoComplete='off'
                    placeholder={t('edit.phoneRequired')}
                    value={form.values?.phone}
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.phone)}
                    helperText={form.errors?.phone}
                  />
                </BaseRow>
                <BaseRow className='gap-5 mb-5 !justify-between !items-center'>
                  <Box className='w-1/2'>
                    <InputLabel>{t('edit.file')}:</InputLabel>
                    <Box className='w-full'>
                      <Box>
                        {form.values.files?.length
                          ? form.values.files.map((file, index) => (
                              <Box className='flex flex-row gap-[10px]'>
                                <Button
                                  className='block font-normal p-0 h-auto truncate'
                                  key={index}
                                  onClick={() => downloadFile(file)}
                                >
                                  • {getFileName(file)}
                                </Button>
                                <IconButton
                                  onClick={() => {
                                    form.setFieldValue(
                                      'files',
                                      form.values.files?.filter((_, fileIndex) => fileIndex !== index)
                                    );
                                  }}
                                >
                                  <DeleteBin6LineIcon fill='#ED1002' />
                                </IconButton>
                              </Box>
                            ))
                          : ''}
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    <Box {...getRootProps()}>
                      <Box component='input' {...getInputProps()} />
                      <Box className='flex w-[216px] items-center justify-center rounded-[8px] p-[7px] border border-[#C6C6C6] cursor-pointer'>
                        <UploadIcon className='w-[20px] h-[20px] mr-auto' />
                        <Box className='mr-auto'>{t('selectFile')}</Box>
                      </Box>
                    </Box>
                    <Box className='text-[11px] w-[216px] text-center'>{t('dragGuide')}</Box>
                  </Box>
                </BaseRow>
              </Box>
            )}

            <Button
              variant='contained'
              color='secondary'
              className='min-w-[125px] flex ml-auto mt-[29px]'
              onClick={() => form.handleSubmit()}
            >
              {t('save')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <DetailFacilityDialog open={isOpenDetailFacilityDialog} data={facility} setOpen={setIsOpenDetailFacilityDialog} />
      <EditFacilityDialog
        open={isOpenEditFacilityDialog}
        mode={editFacilityDialogMode}
        setOpen={setIsOpenEditFacilityDialog}
        onSetRefreshData={setRefreshEditFacilityData}
        locationId={facility?.location_id}
      />
      <ConfirmDialog
        open={isOpenDeleteFacilityDialog}
        title={t('deleteFacilityDialog.title')}
        description={t('deleteFacilityDialog.description', {
          locationName: facility?.location_name,
        })}
        onConfirm={() => handleDeleteFacility(facility?.location_id ?? '')}
        onCancel={() => setIsOpenDeleteFacilityDialog(false)}
      />

      <EditEquipmentDialog
        locationId={facility?.location_id}
        open={isOpenEditEquipmentDialog}
        mode={DIALOG_MODE_CREATE}
        setOpen={setIsOpenEditEquipmentDialog}
        onSetRefreshData={setRefreshEditFacilityData}
      />

      <DetailEquipmentDialog
        open={isOpenDetailEquipmentDialog}
        setOpen={setIsOpenDetailEquipmentDialog}
        data={equipment}
      />

      <ConfirmDialog
        open={isOpenDeleteEquipmentDialog}
        title={t('deleteEquipmentDialog.title')}
        description={t('deleteEquipmentDialog.description', {
          deviceCode: equipment?.device_code,
          userName: props.data?.username,
        })}
        onConfirm={() => handleDeleteDevice()}
        onCancel={() => setIsOpenDeleteEquipmentDialog(false)}
      />
    </Box>
  );
};

export default memo(EditUserDialog);
